import { Link, useParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  BLOG_POSTS,
  formatDate,
  getPost,
  type BlogBlock,
} from '@/content/blog';
import { IconArrowRight, IconChevronRight } from '@/components/home/icons';

/** A single blog article, rendered from structured content blocks. */
export function BlogPost() {
  const { slug } = useParams();
  const post = slug ? getPost(slug) : undefined;
  useDocumentTitle(post ? post.title : 'Article');

  if (!post) {
    return (
      <section className="mx-auto max-w-2xl px-5 pb-24 pt-16 text-center sm:px-8">
        <h1 className="font-display text-2xl font-bold text-night-50">
          Article not found
        </h1>
        <p className="mt-2 text-night-200">
          This article may have moved or been removed.
        </p>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-beam hover:underline"
        >
          <IconChevronRight className="h-4 w-4 rotate-180" />
          Back to the blog
        </Link>
      </section>
    );
  }

  const more = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <article className="mx-auto max-w-2xl px-5 pb-24 pt-12 sm:px-8 sm:pt-16">
      <Link
        to="/blog"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-night-200 transition-colors hover:text-beam-pale"
      >
        <IconChevronRight className="h-4 w-4 rotate-180" />
        All articles
      </Link>

      <header className="mt-6 border-b border-night-500/25 pb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-beam">
          {post.category}
        </p>
        <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-night-50 sm:text-4xl">
          {post.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-night-300">
          <span>{post.author}</span>
          <span>·</span>
          <span>{formatDate(post.date)}</span>
          <span>·</span>
          <span>{post.readingMinutes} min read</span>
        </div>
      </header>

      <div>
        {post.body.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </div>

      <div className="mt-14 border-t border-night-500/25 pt-8">
        <h2 className="font-display text-lg font-bold text-night-50">
          Keep reading
        </h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {more.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="group rounded-xl border border-night-500/40 bg-night-700/60 p-5 transition-colors hover:border-beam/60"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-beam">
                {p.category}
              </p>
              <h3 className="mt-2 font-display text-base font-bold text-night-50">
                {p.title}
              </h3>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-beam">
                Read
                <IconArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </article>
  );
}

function Block({ block }: { block: BlogBlock }) {
  if (block.type === 'h2') {
    return (
      <h2 className="mt-9 font-display text-xl font-bold text-night-50">
        {block.text}
      </h2>
    );
  }
  if (block.type === 'quote') {
    return (
      <figure className="my-8 rounded-2xl border border-night-500/40 bg-night-800/60 p-7 text-center">
        <p
          dir="rtl"
          className="font-arabic text-2xl leading-[1.9] text-night-50"
        >
          {block.text}
        </p>
        <figcaption className="mt-4 font-serif text-base italic text-night-200">
          “{block.translation}”
        </figcaption>
        <p className="mt-2 text-xs uppercase tracking-[0.18em] text-beam">
          {block.source}
        </p>
      </figure>
    );
  }
  return <p className="mt-5 leading-relaxed text-night-200">{block.text}</p>;
}
